import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
   calculateHForWidget,
   renderWidget,
   getWidgetHeight as reportWidgetHeight,
} from "../Widgets/WIdgetsScripts/WidgetScripts";
import { widgetsInterfaces } from "../../containers/WIdgetsSidebar/WIdgetInterfaces";
import { initialWidgetsData } from "../../containers/WIdgetsSidebar/WidgetSidebarData";
import { isActivePublicUser } from "../../util/Scripts";
import { setPropertyInWidget } from "../../appRedux/actions/Boards";
import { DefaultRootState, useSelector } from "react-redux";
import projectConfig from "../../projectConfig.json";

import GridLayout from "react-grid-layout";

import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "./BoardLayout.less";

interface BoardLayoutProps {}

const getResponsiveWidth = (widget: widgetsInterfaces) => {
   if (window.innerWidth > 1200) return widget.xlWidth;
   if (window.innerWidth > 992) return widget.lgWidth;
   if (window.innerWidth > 768) return widget.mdWidth;
   if (window.innerWidth > 576) return widget.smWidth;
   return widget.xsWidth;
};

const getWidgetHeight = (
   widget: widgetsInterfaces,
   widgetHeights: { widgetId: string; widgetHeight: number }[],
   currentBoard: any,
) => {
   const dynamicHeight = widgetHeights?.find((el) => el?.widgetId === widget?.id);
   if (dynamicHeight) {
      return Math.ceil(calculateHForWidget(dynamicHeight.widgetHeight));
   }
   if ("maxHeight" in widget && widget.maxHeight !== null) {
      return widget.maxHeight;
   }
   const defaults: widgetsInterfaces | any = initialWidgetsData(widget.name, currentBoard);
   return defaults?.maxHeight ?? 10;
};

const MeasuredWidget: React.FC<{
   widgetId: string;
   setWidgetHeights: React.Dispatch<
      React.SetStateAction<{ widgetId: string; widgetHeight: number }[]>
   >;
   children: React.ReactNode;
}> = ({ widgetId, setWidgetHeights, children }) => {
   const ref = useRef<HTMLDivElement>(null);
   const lastHeight = useRef(0);

   useEffect(() => {
      const el = ref.current;
      if (!el) return;

      const measure = () => {
         const h = el.scrollHeight || 0;
         if (h && h !== lastHeight.current) {
            lastHeight.current = h;
            reportWidgetHeight(el, widgetId, setWidgetHeights);
         }
      };

      measure();

      const ro = new ResizeObserver(() => measure());
      ro.observe(el);
      return () => ro.disconnect();
   }, [widgetId, setWidgetHeights]);

   return <div ref={ref}>{children}</div>;
};

const BoardLayout: React.FC<BoardLayoutProps> = ({}) => {
   const [currentSheetWidgets, setCurrentSheetWidgets] = useState<widgetsInterfaces[]>([]);
   const [widgetHeights, setWidgetHeights] = useState<{ widgetId: string; widgetHeight: number }[]>(
      [],
   );
   const [containerWidth, setContainerWidth] = useState(0);

   const { settings } = useSelector(({ widgetSettings }) => widgetSettings);
   const { currentBoard, role } = useSelector((state: DefaultRootState) => state.boards);
   const { areWidgetSettingsVisible } = useSelector((state: any) => state.settings);

   const dispatch = useDispatch();
   const divRef = useRef<HTMLDivElement>(null);

   const [isBelow992px, setIsBelow992px] = useState(window.innerWidth <= 992);

   useEffect(() => {
      const mediaQuery = window.matchMedia("(max-width: 992px)");
      const handler = (e) => setIsBelow992px(e.matches);
      mediaQuery.addEventListener("change", handler);
      setIsBelow992px(mediaQuery.matches);
      return () => mediaQuery.removeEventListener("change", handler);
   }, []);

   // Track container width via ResizeObserver so the grid re-renders on resize
   useEffect(() => {
      const el = divRef.current;
      if (!el) return;
      setContainerWidth(el.offsetWidth);

      const ro = new ResizeObserver((entries) => {
         for (const entry of entries) {
            setContainerWidth(entry.contentRect.width);
         }
      });
      ro.observe(el);
      return () => ro.disconnect();
   }, []);

   const availableHandles = (role) => {
      if ((isActivePublicUser() && role === "COLLABORATOR") || !areWidgetSettingsVisible) {
         return [];
      } else {
         return ["s"];
      }
   };

   const onLayoutSave = useCallback(
      (newLayout) => {
         const updatedCurrentSheetWidgets = [...currentSheetWidgets];

         newLayout.forEach((layoutItem) => {
            const correspondingWidget: widgetsInterfaces | any = updatedCurrentSheetWidgets.find(
               (widget: widgetsInterfaces) => widget.id === layoutItem.i,
            );

            if (correspondingWidget) {
               correspondingWidget.positionX = layoutItem.x;
               correspondingWidget.positionY = layoutItem.y;
               correspondingWidget.maxHeight = layoutItem.h;
            }
         });

         updatedCurrentSheetWidgets.forEach((widget: widgetsInterfaces | any) => {
            dispatch(
               setPropertyInWidget({
                  positionX: widget.positionX,
                  positionY: widget.positionY,
                  maxHeight: widget.maxHeight,
               }),
            );
         });
      },
      [currentSheetWidgets, dispatch],
   );

   useEffect(() => {
      const regex = /boards\/\d+\/(\d+)/;
      const matches = regex.exec(window.location.href);

      try {
         if (matches && matches[1]) {
            const currentSheet = matches[1];
            setCurrentSheetWidgets(
               currentBoard.sheets.find((sheet) => sheet.id === parseInt(currentSheet)).widgets,
            );
         }
      } catch (err) {
         console.log(err);
      }
   }, [currentBoard]);

   const sortWidgetsForLayout = <T extends { priority?: number | null; positionY?: number | null }>(
      widgets: T[],
      isBelow992: boolean,
   ): T[] => {
      const copy = [...widgets];

      if (isBelow992) {
         copy.sort((a, b) => {
            const priorityA = a.priority ?? 0;
            const priorityB = b.priority ?? 0;
            return priorityB - priorityA;
         });
      } else {
         copy.sort((a, b) => {
            const positionYA = a.positionY ?? 0;
            const positionYB = b.positionY ?? 0;
            return positionYA - positionYB;
         });
      }

      return copy;
   };

   const widgetsToRender = useMemo(
      () => sortWidgetsForLayout(currentSheetWidgets, isBelow992px),
      [currentSheetWidgets, isBelow992px],
   );

   // Compute the layout array — this is the single source of truth for positions/sizes.
   // Passing it as a prop means react-grid-layout always reflects the latest heights.
   const computedLayout = useMemo(() => {
      return widgetsToRender.map((widget: widgetsInterfaces, index) => {
         const w = parseInt(getResponsiveWidth(widget)) || 24;
         const h = parseInt(getWidgetHeight(widget, widgetHeights, currentBoard)) || 10;

         if (isBelow992px) {
            return {
               i: widget.id,
               x: 0,
               y: index,
               w,
               h,
               isResizable: true,
               resizeHandles: availableHandles(role),
            };
         }

         return {
            i: widget.id,
            x: parseInt(widget.positionX as any) || 0,
            y: parseInt(widget.positionY as any) || 0,
            w,
            h,
            isResizable: true,
            resizeHandles: availableHandles(role),
         };
      });
   }, [widgetsToRender, widgetHeights, currentBoard, isBelow992px, role, areWidgetSettingsVisible]);

   return (
      <div
         ref={divRef}
         style={{
            position: "relative",
         }}
      >
         {containerWidth > 0 && (
            <GridLayout
               className="layout no-resize"
               compactType="vertical"
               cols={24}
               rowHeight={10}
               width={containerWidth}
               layout={computedLayout}
               onDragStop={(newLayout) => onLayoutSave(newLayout)}
               onResizeStop={(newLayout) => onLayoutSave(newLayout)}
               draggableHandle=".widget-dragg-handle"
            >
               {widgetsToRender?.map((widget: widgetsInterfaces) => {
                  return (
                     <div key={widget.id}>
                        {!isActivePublicUser() &&
                           !(role === "COLLABORATOR" || role === undefined) &&
                           areWidgetSettingsVisible && (
                              <div className="widget-dragg-handle">
                                 <span
                                    style={{
                                       fontSize: "17px",
                                    }}
                                    className="material-symbols-outlined"
                                 >
                                    drag_pan
                                 </span>
                              </div>
                           )}
                        <MeasuredWidget widgetId={widget.id} setWidgetHeights={setWidgetHeights}>
                           {renderWidget(widget, role, currentBoard, settings, setWidgetHeights)}
                        </MeasuredWidget>
                     </div>
                  );
               })}
            </GridLayout>
         )}
      </div>
   );
};

export default BoardLayout;
